export interface IEmbeddingModelConfig {
  Provider: string;
  Model: string;
  ChunkingStrategy: string;
  IsProviderBlocks: boolean;
  ModelId: string | null;
}

export enum KBType {
  TEXT = 'text',
  FILE = 'file',
  LINK = 'link',
  QNA = 'qa',
}

export enum EmbeddedStatus {
  PENDING = 'pending',
  COMPLETED = 'completed',
  PROCESSING = 'processing',
  FAILED = 'failed',
}

export interface KBText {
  content: string;
}

export interface KBFile {
  file_id: string;
  system_name: string;
  name: string;
  file_type: number;
  current_version: number;
  additional_properties: Record<string, unknown>;
  content?: string;
}

export interface KBLink {
  url: string;
  content: string;
}

export interface KBQnA {
  question: string;
  answer: string;
}

export interface IKnowledgeBaseItem {
  id: string;
  created_date: string;
  last_updated_date: string;
  created_by: string;
  language: string | null;
  last_updated_by: string;
  organization_ids: string[];
  tags: string[];
  kb_type: KBType;
  agent_id: string;
  file: KBFile | null;
  text: KBText | null;
  qa: KBQnA | null;
  link: KBLink | null;
  is_disabled: boolean;
  is_archived: boolean;
  embad_status: EmbeddedStatus;
  total_chunks: number;
  chunk_size: number;
  meta_data: {
    ProjectKey: string;
    ExternalSource?: {
      system: string;
      label?: string;
      external_id: string;
      title?: string | null;
      url?: string | null;
      revision: number;
      last_synced_at?: string;
    };
  };
  chunk_ext: unknown;
  vector_collection_name?: string;
}

export interface IGetAllKBPayload {
  is_disabled?: boolean;
  is_archived?: boolean;
  project_key: string;
  limit: number;
  offset: number;
  kb_type?: string;
  search?: string;
  agent_id?: string;
  folder_id?: string;
  ids?: string[];
}

export interface IGetAllKBResponse {
  data: {
    knowledge_bases: IKnowledgeBaseItem[];
    total_count: number;
    is_success?: boolean;
  };
  message?: string;
  status_code?: number;
  success?: boolean;
}

export interface ICreateFolderPayload {
  project_key: string;
  name: string;
  description: string;
  embedding_model_config: IEmbeddingModelConfig;
  visibility?: 'private' | 'public';
}

export interface IAddFilesPayload {
  project_key: string;
  agent_id?: string;
  folder_id?: string;
  file_ids: string[];
}

export interface IAddFilesResponse {
  is_success: boolean;
  detail: string;
  error: unknown;
}

export interface IAddTextPayload {
  project_key: string;
  agent_id?: string;
  folder_id?: string;
  text: string;
  visibility?: 'private' | 'public';
}

export interface IAddTextResponse {
  is_success: boolean;
  detail: string;
  error: unknown;
}

export interface IAddQnAPayload {
  project_key: string;
  agent_id?: string;
  folder_id?: string;
  question: string;
  answer: string;
}

export interface IAddQnAResponse {
  is_success: boolean;
  detail: string;
  error: unknown;
}

export interface IAddLinkPayload {
  project_key: string;
  agent_id?: string;
  folder_id?: string;
  link: string;
  allow_recursive: boolean;
}

export interface IAddLinkResponse {
  is_success: boolean;
  detail: string;
  error: unknown;
}

export interface ICreateFolderResponse {
  success: boolean;
  message: string;
  data: {
    folder_id: string;
    name: string;
    description: string;
  };
  status_code: number;
}

export interface IUpdateFolderPayload {
  project_key: string;
  folder_id: string;
  name: string;
  description: string;
  embedding_model_config: IEmbeddingModelConfig;
  visibility?: 'private' | 'public';
}

export interface IUpdateFolderResponse {
  success: boolean;
  message: string;
  data: string;
  status_code: number;
}

export interface IDeleteFolderPayload {
  project_key: string;
  folder_id: string;
}

export interface IDeleteFolderResponse {
  success: boolean;
  message: string;
  data?: string;
  status_code: number;
}

export interface IDeleteKBPayload {
  project_key: string;
  kb_id: string;
}

export interface IDeleteKBResponse {
  is_success?: boolean;
  success?: boolean;
  detail?: string;
  message?: string;
  error?: unknown;
}

export interface IReembedKBItem {
  id: string;
  type: KBType;
}

export interface IReembedKBPayload {
  project_key: string;
  kbs: IReembedKBItem[];
}

export interface IReembedKBResponse {
  is_success?: boolean;
  success?: boolean;
  detail?: string;
  message?: string;
  error?: unknown;
}

export interface IGetAgentsByFolderIdPayload {
  project_key: string;
  folder_id: string;
}

export interface IUpdateKBPayload {
  project_key: string;
  kb_id: string;
  type: KBType;
  content: string;
  question?: string;
}

export interface IUpdateKBResponse {
  is_success: boolean;
  detail: string;
  error: unknown;
}

export interface IGetAgentsByFolderIdResponse {
  success: boolean;
  message: string;
  data: {
    agents: {
      agent_id: string;
      name: string;
    }[];
    total_count: number;
  };
  status_code: number;
}

export interface IFolder {
  _id: string;
  Name: string | null;
  Description: string | null;
  Visibility?: 'private' | 'public' | null;
  visibility?: 'private' | 'public' | null;
  EmbeddingModelConfiguration: IEmbeddingModelConfig;
  CreatedDate: string | null;
  LastUpdatedDate: string | null;
  CreatedBy: string | null;
  /** Server-computed write access (owner or org admin). */
  CanEdit?: boolean | null;
  LastUpdatedBy: string | null;
}

export interface IGetFolderListResponse {
  success: boolean;
  message: string;
  data: {
    folders: IFolder[];
    count: number;
  };
  status_code: number;
}
